// aiControlManager.js - Pure control logic for AI Radio
// Handles user action blocking/unblocking
// Following Rule 4: No over-engineering - simple control state
import logger from '../logger';

/**
 * Control states for AI Radio
 */
export const CONTROL_STATES = {
  NORMAL: 'normal',        // All controls available
  SPEECH_ACTIVE: 'speech', // Block skip/previous during AI preparation and speech
  EMERGENCY: 'emergency'   // Emergency override - all controls blocked briefly
};

/**
 * User actions that can be blocked
 */
export const USER_ACTIONS = {
  SKIP: 'skip',
  PREVIOUS: 'previous',
  NEXT: 'next',
  PAUSE: 'pause',
  PLAY: 'play',
  SEEK: 'seek',
  VOLUME: 'volume',
  TOGGLE_RADIO: 'toggle_radio'
};

/**
 * Playlist modification actions (for targeted transition lock)
 */
export const PLAYLIST_ACTIONS = {
  REMOVE: 'remove',
  MOVE: 'move',
  INSERT: 'insert',
  PLAY_NEXT: 'play_next'
};

/**
 * Control Manager Class - Pure control logic
 * No timing, no audio - just blocking/unblocking decisions
 */
export class AIRadioControlManager {
  constructor() {
    this.state = CONTROL_STATES.NORMAL;
    this.blockedActions = new Set();
    this.emergencyEndTime = 0;
    this.overrideCallbacks = new Map(); // For emergency override detection
    this.transitionLock = null; // { currentIndex, nextIndex } - locks specific track indices
  }

  /**
   * Set control state based on AI Radio timing state
   */
  setControlState(timingState, options = {}) {
    const previousState = this.state;

    switch (timingState) {
      case 'idle':
      case 'ready':
      case 'cooldown':
        this.state = CONTROL_STATES.NORMAL;
        this.blockedActions.clear();
        break;

      case 'preparing':
      case 'speaking':
      case 'transitioning':
        this.state = CONTROL_STATES.SPEECH_ACTIVE;
        this.blockedActions = new Set([
          USER_ACTIONS.SKIP,
          USER_ACTIONS.PREVIOUS,
          USER_ACTIONS.NEXT
        ]);
        break;

      default:
        // Unknown state - default to normal
        this.state = CONTROL_STATES.NORMAL;
        this.blockedActions.clear();
    }

    // Return state change info
    return {
      previousState,
      currentState: this.state,
      changed: previousState !== this.state,
      blockedActions: Array.from(this.blockedActions)
    };
  }

  /**
   * Check if a specific user action should be blocked
   */
  shouldBlockAction(action) {
    const now = Date.now();

    // Emergency state - block everything temporarily
    if (this.state === CONTROL_STATES.EMERGENCY && now < this.emergencyEndTime) {
      return {
        blocked: true,
        reason: 'emergency',
        timeRemaining: this.emergencyEndTime - now
      };
    }

    // Clear emergency state if expired
    if (this.state === CONTROL_STATES.EMERGENCY && now >= this.emergencyEndTime) {
      this.state = CONTROL_STATES.NORMAL;
      this.blockedActions.clear();
    }

    // Check specific action blocking
    if (this.blockedActions.has(action)) {
      return {
        blocked: true,
        reason: 'ai_speech_active',
        state: this.state,
        suggestion: this.getAlternativeSuggestion(action)
      };
    }

    return {
      blocked: false,
      reason: null,
      state: this.state
    };
  }

  /**
   * Get alternative action suggestion when action is blocked
   */
  getAlternativeSuggestion(blockedAction) {
    switch (blockedAction) {
      case USER_ACTIONS.SKIP:
      case USER_ACTIONS.NEXT:
        return 'Turn off AI DJ mode to regain skip control';

      case USER_ACTIONS.PREVIOUS:
        return 'Turn off AI DJ mode to regain previous control';

      default:
        return 'Turn off AI DJ mode for full control';
    }
  }

  /**
   * Emergency override detection
   * Double-tap skip = force disable radio mode
   */
  detectEmergencyOverride(action, timestamp = Date.now()) {
    // Only track skip actions for emergency override
    if (action !== USER_ACTIONS.SKIP) {
      return { isEmergency: false };
    }

    const actionKey = 'last_skip_attempt';
    const lastAttempt = this.overrideCallbacks.get(actionKey);

    // First skip attempt
    if (!lastAttempt) {
      this.overrideCallbacks.set(actionKey, timestamp);
      return { isEmergency: false, firstAttempt: true };
    }

    // Check for double-tap (within 1 second)
    const timeDiff = timestamp - lastAttempt;
    if (timeDiff < 1000) {
      // Emergency override detected
      this.triggerEmergency(2000); // 2 second emergency state
      this.overrideCallbacks.delete(actionKey);

      return {
        isEmergency: true,
        timeDiff,
        action: 'force_disable_radio'
      };
    }

    // Update last attempt time
    this.overrideCallbacks.set(actionKey, timestamp);
    return { isEmergency: false, secondAttempt: true };
  }

  /**
   * Trigger emergency state (briefly block all actions)
   */
  triggerEmergency(durationMs = 1000) {
    this.state = CONTROL_STATES.EMERGENCY;
    this.emergencyEndTime = Date.now() + durationMs;
    this.blockedActions = new Set(Object.values(USER_ACTIONS));

    return {
      state: CONTROL_STATES.EMERGENCY,
      endTime: this.emergencyEndTime,
      duration: durationMs
    };
  }

  /**
   * Force clear all blocking (emergency situation)
   */
  clearAllBlocking() {
    this.state = CONTROL_STATES.NORMAL;
    this.blockedActions.clear();
    this.emergencyEndTime = 0;
    this.overrideCallbacks.clear();
    this.transitionLock = null;

    return { cleared: true, state: this.state };
  }

  /**
   * Get current control status
   */
  getStatus() {
    return {
      state: this.state,
      blockedActions: Array.from(this.blockedActions),
      emergencyActive: this.state === CONTROL_STATES.EMERGENCY,
      emergencyEndTime: this.emergencyEndTime,
      transitionLock: this.transitionLock
    };
  }

  /**
   * Check if any controls are currently blocked
   */
  hasBlockedActions() {
    return this.blockedActions.size > 0 || this.state === CONTROL_STATES.EMERGENCY;
  }

  /**
   * Get user-friendly control status message
   */
  getStatusMessage() {
    if (this.state === CONTROL_STATES.EMERGENCY) {
      return 'Emergency override in progress...';
    }

    if (this.state === CONTROL_STATES.SPEECH_ACTIVE) {
      return 'AI DJ preparing/speaking - skip controls temporarily disabled';
    }

    return 'All controls available';
  }

  /**
   * Set transition lock - locks specific track indices during DJ speech
   */
  setTransitionLock(currentIndex, nextIndex) {
    this.transitionLock = { currentIndex, nextIndex };
    logger.debug(`Transition lock set: current=${currentIndex}, next=${nextIndex}`);
    return this.transitionLock;
  }

  /**
   * Clear transition lock
   */
  clearTransitionLock() {
    logger.debug('Transition lock cleared');
    this.transitionLock = null;
  }

  /**
   * Check if a playlist action should be blocked by transition lock
   */
  shouldBlockPlaylistAction(action, targetIndex, insertIndex = null) {
    if (!this.transitionLock) {
      return { blocked: false };
    }

    const { currentIndex, nextIndex } = this.transitionLock;

    // Block if targeting locked indices (remove or move TO these positions)
    if (targetIndex === currentIndex || targetIndex === nextIndex) {
      return { blocked: true, reason: 'Track locked during DJ transition' };
    }

    // Block insert between current and next
    if (action === PLAYLIST_ACTIONS.INSERT && insertIndex === nextIndex) {
      return { blocked: true, reason: 'Cannot insert during DJ transition' };
    }

    // Block play_next (inserts at next position)
    if (action === PLAYLIST_ACTIONS.PLAY_NEXT) {
      return { blocked: true, reason: 'Cannot play next during DJ transition' };
    }

    return { blocked: false };
  }
}

/**
 * Control utilities
 */
export const ControlUtils = {
  /**
   * Check if action is a critical control (needs special handling)
   */
  isCriticalAction(action) {
    return [
      USER_ACTIONS.SKIP,
      USER_ACTIONS.PREVIOUS,
      USER_ACTIONS.NEXT
    ].includes(action);
  },

  /**
   * Check if action is always allowed (even during speech)
   */
  isAlwaysAllowed(action) {
    return [
      USER_ACTIONS.VOLUME,
      USER_ACTIONS.TOGGLE_RADIO
    ].includes(action);
  },

  /**
   * Get blocked action display name
   */
  getActionDisplayName(action) {
    const names = {
      [USER_ACTIONS.SKIP]: 'Skip Track',
      [USER_ACTIONS.PREVIOUS]: 'Previous Track',
      [USER_ACTIONS.NEXT]: 'Next Track',
      [USER_ACTIONS.PAUSE]: 'Pause',
      [USER_ACTIONS.PLAY]: 'Play',
      [USER_ACTIONS.SEEK]: 'Seek',
      [USER_ACTIONS.VOLUME]: 'Volume',
      [USER_ACTIONS.TOGGLE_RADIO]: 'Toggle AI DJ'
    };

    return names[action] || action;
  },

  /**
   * Create user notification for blocked action
   */
  createBlockedActionNotification(action, blockInfo) {
    const actionName = this.getActionDisplayName(action);

    if (blockInfo.reason === 'emergency') {
      return {
        type: 'warning',
        title: 'Emergency Override',
        message: 'Please wait...',
        duration: blockInfo.timeRemaining
      };
    }

    if (blockInfo.reason === 'ai_speech_active') {
      return {
        type: 'info',
        title: `${actionName} Temporarily Disabled`,
        message: blockInfo.suggestion,
        action: 'Turn Off AI DJ'
      };
    }

    return {
      type: 'error',
      title: `${actionName} Blocked`,
      message: 'Unknown reason',
      duration: 3000
    };
  }
};

/**
 * Factory function
 */
export function createControlManager() {
  return new AIRadioControlManager();
}

export default {
  AIRadioControlManager,
  CONTROL_STATES,
  USER_ACTIONS,
  ControlUtils,
  createControlManager
};